'use client';

import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';

import { Button } from '@platform/ui/components/button';
import { Field } from '@platform/ui/components/field';
import { Input } from '@platform/ui/components/input';
import { Modal } from '@platform/ui/components/modal';

/**
 * What a confirmation asks. The title says what will happen ("Depo silinecek"),
 * the body the consequence, the action is a verb (docs/11 §7). "Emin misiniz?"
 * is not a title.
 */
export interface ConfirmOptions {
    title: ReactNode;
    body?: ReactNode;
    /** The verb on the primary button. Defaults to the provider's confirm label. */
    action?: string;
    cancel?: string;
    /** A destructive decision gets the danger treatment: outlined, error-coloured. */
    tone?: 'danger';
    /**
     * When set, the dialog asks for a line of text and resolves with it — a
     * rejection reason, a cancellation note, something a marketplace will read.
     */
    input?: {
        label: string;
        placeholder?: string;
        required?: boolean;
        initial?: string;
    };
}

export interface ConfirmLabels {
    confirm: string;
    cancel: string;
    close: string;
}

export function ConfirmDialog({
    options,
    onResolve,
    labels,
}: {
    options: ConfirmOptions;
    /** Resolves with true (or the typed text) on confirm, false on cancel. */
    onResolve: (value: string | boolean) => void;
    labels: ConfirmLabels;
}) {
    const [text, setText] = useState(options.input?.initial ?? '');
    const disabled = Boolean(options.input?.required) && text.trim() === '';
    const confirm = () => onResolve(options.input ? text.trim() : true);
    const cancel = () => onResolve(false);

    return (
        <Modal
            open
            onClose={cancel}
            title={options.title}
            description={options.body}
            tone={options.tone}
            closeLabel={labels.close}
            footer={
                <>
                    <Button type="button" variant="ghost" onClick={cancel}>
                        {options.cancel ?? labels.cancel}
                    </Button>
                    <Button type="button" variant={options.tone === 'danger' ? 'danger' : 'primary'} disabled={disabled} onClick={confirm}>
                        {options.action ?? labels.confirm}
                    </Button>
                </>
            }
        >
            {options.input && (
                <form
                    noValidate
                    onSubmit={(event) => {
                        event.preventDefault();
                        if (!disabled) confirm();
                    }}
                >
                    <Field label={options.input.label} required={options.input.required}>
                        {({ id }) => (
                            <Input id={id} value={text} placeholder={options.input?.placeholder} onChange={(event) => setText(event.target.value)} />
                        )}
                    </Field>
                </form>
            )}
        </Modal>
    );
}

type ConfirmFn = {
    (options: ConfirmOptions & { input?: undefined }): Promise<boolean>;
    (options: ConfirmOptions & { input: NonNullable<ConfirmOptions['input']> }): Promise<string | null>;
};

const ConfirmContext = createContext<ConfirmFn | null>(null);

/**
 * Hosts the one confirmation dialog an application needs, replacing
 * `window.confirm`. Ask with `const ok = await confirm({ title, body, action })`;
 * the promise resolves when the person decides, so calling code reads like the
 * old `if (!confirm(...)) return`.
 */
export function ConfirmProvider({ labels, children }: { labels: ConfirmLabels; children: ReactNode }) {
    const [state, setState] = useState<{ options: ConfirmOptions; resolve: (value: string | boolean) => void } | null>(null);

    const confirm = useCallback((options: ConfirmOptions) => {
        return new Promise<string | boolean | null>((resolve) => {
            setState({
                options,
                resolve: (value) => {
                    setState(null);
                    if (options.input) resolve(value === false ? null : (value as string));
                    else resolve(Boolean(value));
                },
            });
        });
    }, []) as unknown as ConfirmFn;

    return (
        <ConfirmContext.Provider value={confirm}>
            {children}
            {state && <ConfirmDialog options={state.options} onResolve={state.resolve} labels={labels} />}
        </ConfirmContext.Provider>
    );
}

export function useConfirm(): ConfirmFn {
    const confirm = useContext(ConfirmContext);
    if (!confirm) throw new Error('useConfirm must be used inside a ConfirmProvider');
    return confirm;
}
